import type { ReactNode } from "react";
import { CAMPO_BASE } from "./ui";

// A barra de filtros das telas de administração — UMA peça, três painéis.
//
// ---------------------------------------------------------------------------
// Por que ela existe
// ---------------------------------------------------------------------------
// Pedido de Victor em 09/09/2026, olhando o balcão da secretaria: a busca numa
// linha só, o botão "Buscar" virando lupa dentro do campo, e os filtros num
// lugar só da tela. Depois, olhando as outras duas sub-abas: "quero a barra
// completa — datas e prazo — também em Notas e Matrículas".
//
// Antes, cada painel tinha a sua forma: o balcão espalhava os controles em três
// faixas (busca à direita do título, chips de fluxo, um cartão de combos), e
// Notas e Matrículas usavam uma grade de combos com rótulo em cima. Três telas
// da mesma aba, três desenhos. Copiar o do balcão nas outras duas duplicaria a
// decisão de largura, de contraste e de rótulo em três arquivos — e a que
// divergisse em silêncio seria justamente a que ninguém abre.
//
// ---------------------------------------------------------------------------
// O que a barra NÃO faz
// ---------------------------------------------------------------------------
// Ela não tem estado e não conhece filtro nenhum: recebe os controles como
// filhos. Quem sabe o que cada filtro significa é o painel — e é o painel que
// põe cada valor na QUERY da rota, para o operador poder mandar a tela a um
// colega (a mesma razão de a sub-aba ser sub-rota).

/**
 * O painel — um `surface` com as linhas de controle dentro.
 *
 * `data-painel-filtros` é o escopo que as provas usam para cobrar que NENHUM
 * controle de filtro ficou fora daqui. Sem o atributo, a asserção teria de
 * procurar por texto, e é assim que se acha o elemento errado (a cicatriz das
 * sete etiquetas iguais da calculadora).
 */
export function BarraDeFiltros({ children }: { children: ReactNode }) {
  return (
    <div className="surface flex flex-col gap-3 p-4" data-painel-filtros>
      {children}
    </div>
  );
}

/** Uma linha de controles da barra — quebra sozinha nas telas estreitas. */
export function LinhaDeFiltros({ n, children }: { n: number; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3" data-linha-filtros={String(n)}>
      {children}
    </div>
  );
}

/**
 * Um combo da barra, SEM rótulo visível.
 *
 * O rótulo mora na opção "todos" — "Todos os status", "Todas as turmas" —, que
 * nomeia o combo e ainda diz o que ele faz quando ninguém escolheu. O nome
 * acessível vai no `aria-label`, então quem usa leitor de tela recebe a mesma
 * informação.
 *
 * **Sem contagem nas opções**, e isso é decisão de Victor em 09/09 olhando a
 * tela. Os combos da fila diziam "(11.921)" em cada opção desde a 000159, e o
 * número era da fila do FLUXO inteiro — escolhido um curso, ele não mudava.
 * Contagem ao lado de uma opção afirma "escolhendo isto, você vê N", e isso só é
 * verdade se N acompanhar os outros filtros ativos. O que sobrou dela é a
 * decisão de ESCONDER a opção que não tem item nenhum na base — filtro que leva
 * a zero é pior que filtro nenhum.
 */
export function Filtro({
  rotulo,
  rotuloTodos,
  valor,
  opcoes,
  aoTrocar,
  desligado = false,
  chave,
}: {
  /** O nome ACESSÍVEL do combo. */
  rotulo: string;
  /** O texto da opção "todos": "Todos os status", "Todas as turmas"… */
  rotuloTodos: string;
  valor: string | null;
  /** `itens` opcional: quando vem, opção com zero não aparece. */
  opcoes: { valor: string; rotulo: string; itens?: number }[];
  aoTrocar: (v: string | null) => void;
  /**
   * Desligado de propósito, com o motivo DITO pela tela ao lado.
   *
   * Não é `opacity`: desabilitado PERMANENTE pede desenho próprio, não
   * transparência (a medição que criou a `pill-desligada` — `opacity-50` sobre
   * navy dá 1,57 : 1).
   */
  desligado?: boolean;
  /**
   * O `data-filtro` da prova. Sem ele o seletor sai do rótulo, e rótulo muda
   * com o texto — o atributo é o contrato.
   */
  chave: string;
}) {
  const visiveis = opcoes.filter((o) => o.itens === undefined || o.itens > 0);
  return (
    <select
      value={valor ?? ""}
      onChange={(e) => aoTrocar(e.target.value || null)}
      aria-label={rotulo}
      title={desligado ? `${rotulo}: o dado ainda não foi carregado` : rotulo}
      disabled={desligado}
      data-filtro={chave}
      // `CAMPO_BASE` e não `CAMPO`: o `w-full` dele venceria o `flex-1`, e cada
      // combo ocuparia uma linha. 14rem é o que "Todas as instituições" precisa
      // para não truncar.
      className={`${CAMPO_BASE} min-w-[14rem] flex-1 text-sm ${
        desligado ? "border-dashed text-cinza" : ""
      }`}
    >
      <option value="">{rotuloTodos}</option>
      {visiveis.map((o) => (
        <option key={o.valor} value={o.valor}>
          {o.rotulo}
        </option>
      ))}
    </select>
  );
}

/**
 * Um campo de data da barra.
 *
 * `min`/`max` vêm das PONTAS do que existe na base (o banco as devolve junto
 * dos filtros): um calendário aberto em 1970 convida a filtrar o nada, e o
 * resultado vazio parece defeito da tela.
 *
 * Largura fixa e `flex-none`: o campo de data nativo tem o ícone do calendário
 * dentro, e esticá-lo com `flex-1` deixa o ícone longe do texto.
 */
export function CampoDeData({
  rotulo,
  valor,
  aoTrocar,
  min,
  max,
  chave,
}: {
  rotulo: string;
  valor: string | null;
  aoTrocar: (v: string | null) => void;
  min?: string | null;
  max?: string | null;
  chave: string;
}) {
  return (
    <input
      type="date"
      aria-label={rotulo}
      title={rotulo}
      value={valor ?? ""}
      min={min ?? undefined}
      max={max ?? undefined}
      onChange={(e) => aoTrocar(e.target.value || null)}
      data-filtro={chave}
      className={`${CAMPO_BASE} w-40 flex-none`}
    />
  );
}

/**
 * O botão de limpar a barra inteira.
 *
 * Aparece só quando há algo para limpar — botão sempre presente e sem efeito
 * ensina a ignorá-lo. E limpa a BUSCA junto: com os dois separados, limpar os
 * filtros e continuar vendo a lista de uma busca antiga é a tela contradizendo
 * o próprio painel.
 */
export function LimparFiltros({ aoLimpar, texto = "limpar filtros e busca" }: {
  aoLimpar: () => void;
  texto?: string;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={aoLimpar}
        data-limpar-filtros
        // `alerta-tinta` e não `laranja-texto`: é texto pequeno sobre fundo
        // claro, e #c76a00 dá 3,83 : 1 (a medição do runbook dos cursos bônus).
        className="text-sm font-semibold text-alerta-tinta underline-offset-4 hover:underline"
      >
        {texto}
      </button>
    </div>
  );
}

/**
 * `aaaa-mm-dd` — o único formato que o `<input type="date">` entrega, e o que o
 * banco compara em `date`. Query digitada à mão com outra coisa cai em nulo, que
 * é "sem filtro": um valor que a tela não sabe mostrar não pode produzir uma
 * lista que ela não sabe explicar.
 */
export const DATA_ISO = /^\d{4}-\d{2}-\d{2}$/;
export const dataDaQuery = (v: string | null) => (DATA_ISO.test(v ?? "") ? v : null);
